import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Users, Star, MapPin } from 'lucide-react'
import type { Dataset, Property, FinancialPeriod } from '@/types/entities'
import { SectionCard, EmptyState, Badge, Delta, Button } from '@/components/ui'
import { DonutGauge, Sparkline, StackedBar, AgingBars } from '@/components/charts'
import { C } from '@/lib/colors'
import { latest, accountById, fundById } from '@/lib/selectors'
import { money, num, pct, date } from '@/lib/format'
import { delinquencyRead, netAbsorption, trendDelta } from './propertyMetrics'
import { PropertyKeyInfoForm } from './PropertyKeyInfoForm'

export function OverviewTab({ d, p, fp }: { d: Dataset; p: Property; fp?: FinancialPeriod }) {
  const [keyInfoOpen, setKeyInfoOpen] = useState(false)
  // Prefer the trailing-30-day DAILY series for the trend chart; the
  // monthly snapshots remain the fallback (seed-only assets) and still
  // drive the year-scale sparklines elsewhere.
  const trendSource = p.occupancyDaily?.length ? p.occupancyDaily : p.occupancyPct
  const occSnaps = [...(trendSource ?? [])].sort((a, b) => (a.date < b.date ? -1 : 1))
  // "Live" = data through YESTERDAY or today — as current as a T+1 daily
  // feed can be. Anything older shows a plain gray date instead: calling
  // two-day-old data "live" is a lie (Chauncey, 2026-07-14 — upstream ETL
  // lag made a 7/12 endpoint wear a LIVE badge on the 14th).
  const lastSnapDate = occSnaps[occSnaps.length - 1]?.date
  const isLive = !!lastSnapDate
    && Date.now() - new Date(`${lastSnapDate}T00:00:00`).getTime() < 2 * 86_400_000
  const occLatest = latest(trendSource) ?? latest(p.occupancyPct)
  const occDelta = trendDelta(trendSource)
  const owner = accountById(d, p.ownerAccountId)
  const fund = fundById(d, p.fundId)
  const del = delinquencyRead(fp)
  const absorption = netAbsorption(p)

  // Rate health derived from latest revenue + occupied sqft (annualized economic occupancy proxy).
  const revLatest = latest(p.revenue)
  const occSqFt = latest(p.occupiedSqFt)
  const grossPotentialRate = p.nrsf && revLatest ? (revLatest * 12) / p.nrsf : undefined // $/nrsf/yr at full
  const actualOccupiedRate = occSqFt && revLatest ? (revLatest * 12) / occSqFt : undefined // $/occ sf/yr
  const effectiveRate = revLatest && p.totalUnits ? (revLatest / p.totalUnits) : undefined // $/unit/mo

  return (
    <div className="pd-stack">
      {/* Row 1: Occupancy & activity + Rate health */}
      <div className="pd-grid pd-grid--2-1">
        <SectionCard eyebrow="Operations" title="Occupancy & Activity">
          {occLatest == null ? (
            <EmptyState title="No occupancy snapshots" hint="No physical-occupancy time series on file for this asset." />
          ) : (
            <>
              <div className="pd-occ">
                <DonutGauge
                  value={occLatest}
                  size={120}
                  color={occLatest >= 90 ? C.ok : occLatest >= 85 ? C.cyan : C.warn}
                  sub="Physical occupancy"
                />
                <div className="pd-occ-trend">
                  <div className="pd-occ-trend-head">
                    <span className="label">{p.occupancyDaily?.length ? 'Occupancy · Trailing 30 Days' : 'Occupancy Trend'}</span>
                    {occDelta != null && (
                      <Delta value={occDelta} higherIsBetter>{`${occDelta >= 0 ? '+' : ''}${occDelta.toFixed(1)} pts`}</Delta>
                    )}
                  </div>
                  <Sparkline
                    data={occSnaps.map((s) => s.value)}
                    stretch
                    height={56}
                    color={C.navy}
                    endDotColor={isLive ? C.ok : undefined}
                  />
                  <div className="pd-spark-foot">
                    <span>{date(occSnaps[0]?.date, 'short')}</span>
                    <span className={isLive ? 'pd-spark-live' : undefined}>
                      {isLive && <span className="pd-live-dot" />}
                      {date(occSnaps[occSnaps.length - 1]?.date, 'short')}
                      {isLive ? ' · live' : ''}
                    </span>
                  </div>
                </div>
              </div>
              <div className="pd-flow">
                <div className="pd-flow-item">
                  <span className="pd-flow-val num">{num(occSqFt)}</span>
                  <span className="pd-flow-label">Occupied SF</span>
                </div>
                <div className="pd-flow-item">
                  <span className="pd-flow-val num">{absorption != null ? `${absorption >= 0 ? '+' : ''}${num(absorption)}` : '—'}</span>
                  <span className="pd-flow-label">Net Absorption (window)</span>
                </div>
                <div className="pd-flow-item">
                  <span className="pd-flow-val num">{num(p.totalUnits)}</span>
                  <span className="pd-flow-label">Total Units</span>
                </div>
              </div>
            </>
          )}
        </SectionCard>

        <SectionCard eyebrow="Pricing" title="Rate Health">
          <div className="pd-rate">
            <div className="pd-rate-row">
              <div className="pd-rate-label">
                Gross Potential Rate
                <small>Annualized in-place revenue ÷ NRSF</small>
              </div>
              <div className="pd-rate-val">
                {grossPotentialRate != null ? `$${grossPotentialRate.toFixed(2)}` : '—'}
                <span className="pd-unit">/sf/yr</span>
              </div>
            </div>
            <div className="pd-rate-row">
              <div className="pd-rate-label">
                Actual Occupied Rate
                <small>Annualized revenue ÷ occupied SF</small>
              </div>
              <div className="pd-rate-val">
                {actualOccupiedRate != null ? `$${actualOccupiedRate.toFixed(2)}` : '—'}
                <span className="pd-unit">/occ sf/yr</span>
              </div>
            </div>
            <div className="pd-rate-row">
              <div className="pd-rate-label">
                Effective Rate / Unit
                <small>Monthly revenue ÷ total units</small>
              </div>
              <div className="pd-rate-val">
                {effectiveRate != null ? money(effectiveRate, { cents: true }) : '—'}
                <span className="pd-unit">/unit/mo</span>
              </div>
            </div>
          </div>
          {revLatest == null && (
            <p className="pd-caption">No revenue snapshots — rate health unavailable.</p>
          )}
        </SectionCard>
      </div>

      {/* Row 2: Unit mix + Delinquency */}
      <div className="pd-grid pd-grid--2-1">
        <SectionCard eyebrow="Physical" title="Unit Mix">
          {p.unitMix && p.unitMix.length > 0 ? (
            <>
              <StackedBar
                segments={p.unitMix.map((u) => ({ label: u.size, value: u.count }))}
                height={14}
              />
              <p className="pd-caption">
                {num(p.totalUnits)} units · {num(p.nrsf)} NRSF
                {p.unitTypes?.length ? ` · ${p.unitTypes.join(', ')}` : ''}
              </p>
            </>
          ) : (
            <EmptyState title="No unit-mix detail" hint="Bucketed unit-mix breakdown not captured for this asset." />
          )}
        </SectionCard>

        <SectionCard eyebrow="Receivables" title="Delinquency">
          {del.pct != null ? (
            <>
              <div className="pd-bigstat">
                <span className="pd-bigstat-val num">{pct(del.pct)}</span>
                <span className="pd-bigstat-unit">of in-place rent</span>
              </div>
              <div style={{ marginTop: 'var(--sp-4)' }}>
                <AgingBars
                  buckets={[
                    {
                      label: 'Bad debt (Q1)',
                      value: del.amount ?? 0,
                      tone: del.pct >= 20 ? 'alert' : del.pct >= 8 ? 'warn' : 'ok',
                    },
                  ]}
                />
              </div>
              <p className="pd-caption">Derived from Q1 bad-debt vs. gross rental income (Yardi GL).</p>
            </>
          ) : (
            <EmptyState title="No delinquency data" hint="Requires an attached financial period. Aging detail not mirrored for this asset." />
          )}
        </SectionCard>
      </div>

      {/* Row 3: Key information + Demographics */}
      <div className="pd-grid pd-grid--2-1">
        <SectionCard
          eyebrow="Reference"
          title="Key Information"
          right={<Button variant="ghost" onClick={() => setKeyInfoOpen(true)}>Edit</Button>}
        >
          <KeyInfo p={p} owner={owner} fund={fund} />
          <PropertyKeyInfoForm open={keyInfoOpen} onClose={() => setKeyInfoOpen(false)} property={p} />
        </SectionCard>

        <div className="pd-stack">
          <SectionCard
            eyebrow="Market"
            title="Demographics"
            right={<Badge tone="neutral" variant="outline">{<><Users size={11} /> Trade Area</>}</Badge>}
          >
            {p.demographics && p.demographics.length > 0 ? (
              <table className="pd-demo">
                <thead>
                  <tr>
                    <th>Radius</th>
                    <th>Population</th>
                    <th>Households</th>
                    <th>Median Inc.</th>
                  </tr>
                </thead>
                <tbody>
                  {[...p.demographics].sort((a, b) => a.radiusMi - b.radiusMi).map((r) => (
                    <tr key={r.radiusMi}>
                      <td>{r.radiusMi} mi</td>
                      <td className="num">{num(r.population)}</td>
                      <td className="num">{num(r.households)}</td>
                      <td className="num">{money(r.medianIncome, { compact: true })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState
                icon={<MapPin size={26} />}
                title="No demographics on file"
                hint="Trade-area rings not pulled for this asset."
              />
            )}
            {(p.numCompetitors != null || p.numREITs != null) && (
              <p className="pd-caption">
                {p.numCompetitors != null ? `${num(p.numCompetitors)} competitors` : ''}
                {p.numCompetitors != null && p.numREITs != null ? ' · ' : ''}
                {p.numREITs != null ? `${num(p.numREITs)} REIT-operated within radius` : ''}
              </p>
            )}
          </SectionCard>

          <ReputationCard p={p} />
        </div>
      </div>
    </div>
  )
}

function KeyInfo({
  p, owner, fund,
}: {
  p: Property
  owner?: ReturnType<typeof accountById>
  fund?: ReturnType<typeof fundById>
}) {
  const a = p.address
  const addr = a ? [a.line1, [a.city, a.state].filter(Boolean).join(', '), a.zip].filter(Boolean).join(' · ') : undefined
  const ext = p.externalIds ?? {}
  const externalRows: { label: string; value: React.ReactNode }[] = []
  if (ext.siteId != null) externalRows.push({ label: 'SiteLink ID', value: <span className="num">{ext.siteId}</span> })
  if (ext.storTrackStoreId != null) externalRows.push({ label: 'StorTrack Store', value: <span className="num">{ext.storTrackStoreId}</span> })
  if (ext.birdEyeId != null) externalRows.push({ label: 'BirdEye ID', value: <span className="num">{ext.birdEyeId}</span> })
  if (ext.serviceChannelId != null) externalRows.push({ label: 'ServiceChannel', value: <span className="num">{ext.serviceChannelId}</span> })
  if (ext.callPotentialId != null) externalRows.push({ label: 'CallPotential', value: <span className="num">{ext.callPotentialId}</span> })

  return (
    <dl className="dl dl--1">
      <Row label="Address" value={addr} />
      <Row
        label="Owner Account"
        value={owner ? <Link className="pd-link" to={`/accounts/${owner.id}`}>{owner.name}</Link> : '—'}
      />
      <Row
        label="Fund"
        value={fund ? <Link className="pd-link" to={`/funds/${fund.id}`}>{fund.name}</Link> : '—'}
      />
      <Row label="Managing Entity" value={p.managingEntity} />
      <Row label="Brand" value={p.brand} />
      <Row label="Current Owner" value={p.currentOwner} />
      <Row label="Relationship" value={p.relationshipType} />
      {p.managementFeePct != null && <Row label="Mgmt Fee" value={pct(p.managementFeePct)} />}
      <Row label="Onboarded" value={p.onboardedDate ? date(p.onboardedDate, 'long') : undefined} />
      <Row label="Stabilized" value={p.stabilizedDate ? date(p.stabilizedDate, 'long') : undefined} />
      <Row label="L-Code" value={ext.lcode ? <span className="num">{ext.lcode}</span> : undefined} />
      <Row label="Yardi Code" value={ext.yardiCode ? <span className="num">{ext.yardiCode}</span> : undefined} />
      {externalRows.map((r) => <Row key={String(r.label)} label={r.label} value={r.value} />)}
    </dl>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="dl-row">
      <dt className="dl-label label">{label}</dt>
      <dd className="dl-value">{value || <span className="dim">—</span>}</dd>
    </div>
  )
}

function ReputationCard({ p }: { p: Property }) {
  const rating = latest(p.googleRating)
  const reviews = latest(p.googleReviewCount)
  return (
    <SectionCard
      eyebrow="Reputation"
      title="Online Reputation"
      right={<Badge tone="neutral" variant="outline">{<><Star size={11} /> Google</>}</Badge>}
    >
      {rating != null ? (
        <>
          <div className="pd-rep">
            <span className="pd-rep-score num">{rating.toFixed(1)}</span>
            <div>
              <div className="pd-rep-stars">{'★'.repeat(Math.round(rating))}{'☆'.repeat(5 - Math.round(rating))}</div>
              <div className="pd-rep-meta">{reviews != null ? `${num(reviews)} reviews` : 'review count unavailable'}</div>
            </div>
          </div>
          <p className="pd-caption">Estimated from public Google Business Profile signals — directional, not audited.</p>
        </>
      ) : (
        <EmptyState icon={<Star size={26} />} title="No reputation signal" hint="Google rating not captured for this asset." />
      )}
    </SectionCard>
  )
}
